import React, { useState, useEffect } from 'react';
import { FiX, FiSearch, FiCheck } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { recitalService } from '../../../services/RecitalService';

const DAY_ABBR = { monday:'Mon', tuesday:'Tue', wednesday:'Wed', thursday:'Thu', friday:'Fri', saturday:'Sat', sunday:'Sun' };

const S = {
    overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' },
    modal:   { background:'#fff', borderRadius:14, width:540, maxWidth:'95vw', maxHeight:'90vh', display:'flex', flexDirection:'column', overflow:'hidden' },
    header:  { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 24px 0', borderBottom:'1px solid #e0e0ec', paddingBottom:16 },
    title:   { fontSize:17, fontWeight:700, color:'#3f3f55', margin:0 },
    closeBtn:{ background:'none', border:'none', cursor:'pointer', color:'#6b6b8a', display:'flex', padding:4 },
    body:    { padding:'24px', overflowY:'auto', flex:1 },
    label:   { fontSize:13, fontWeight:600, color:'#3f3f55', marginBottom:6, display:'block' },
    input:   { width:'100%', border:'1px solid #e0e0ec', borderRadius:8, padding:'10px 12px', fontSize:14, color:'#3f3f55', outline:'none', boxSizing:'border-box' },
    select:  { width:'100%', border:'1px solid #e0e0ec', borderRadius:8, padding:'10px 12px', fontSize:14, color:'#3f3f55', outline:'none', boxSizing:'border-box', background:'#fff', cursor:'pointer' },
    optWrap: { display:'flex', flexDirection:'column', gap:8, marginTop:8 },
    opt:     (active) => ({ border: `2px solid ${active ? '#29b8e5' : '#e0e0ec'}`, borderRadius:10, padding:'12px 16px',
               cursor:'pointer', background: active ? '#e6f7fd' : '#fff', transition:'all 0.15s' }),
    optTitle:{ fontSize:14, fontWeight:600, color:'#3f3f55' },
    optSub:  { fontSize:12, color:'#6b6b8a', marginTop:2 },
    footer:  { display:'flex', justifyContent:'flex-end', gap:10, padding:'16px 24px', borderTop:'1px solid #e0e0ec' },
    btnSecondary: { background:'none', border:'1px solid #e0e0ec', borderRadius:8, padding:'9px 20px', fontSize:14, cursor:'pointer', color:'#3f3f55' },
    btnPrimary:   { background:'#29b8e5', border:'none', borderRadius:8, padding:'9px 20px', fontSize:14, fontWeight:600, color:'#fff', cursor:'pointer' },
    search:  { position:'relative', marginBottom:12 },
    searchIcon: { position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'#6b6b8a' },
    searchInput:{ width:'100%', border:'1px solid #e0e0ec', borderRadius:8, padding:'9px 12px 9px 34px', fontSize:13, outline:'none', boxSizing:'border-box' },
    classRow:{ display:'flex', alignItems:'center', gap:12, padding:'10px 12px', borderRadius:8, cursor:'pointer', marginBottom:4 },
    swatch:  (color) => ({ width:14, height:14, borderRadius:3, background: color || '#e0e0ec', flexShrink:0 }),
    className:{ fontSize:14, fontWeight:600, color:'#3f3f55', flex:1 },
    classMeta:{ fontSize:11, color:'#6b6b8a' },
    dayChip: { fontSize:10, background:'#f0f0f8', color:'#3f3f55', borderRadius:4, padding:'2px 6px' },
    checkbox:{ width:18, height:18, borderRadius:4, border:'2px solid #e0e0ec', display:'flex', alignItems:'center', justifyContent:'center',
               flexShrink:0, background:'#fff' },
    checked: { background:'#29b8e5', borderColor:'#29b8e5' },
};

const IMPORT_OPTS = [
    { value:'all',    title:'Import all classes',       sub:'All classes from the selected schedule' },
    { value:'choose', title:'Choose specific classes',  sub:'Pick exactly which classes to include'  },
    { value:'empty',  title:'Start empty',              sub:'Add classes manually later'             },
];

export default function CreateRecitalModal({ onClose, onCreated }) {
    const [step, setStep]             = useState(1);
    const [name, setName]             = useState('');
    const [importOpt, setImportOpt]   = useState('all');
    const [boards, setBoards]         = useState([]);
    const [boardId, setBoardId]       = useState('');
    const [classes, setClasses]       = useState([]);
    const [selected, setSelected]     = useState(new Set());
    const [search, setSearch]         = useState('');
    const [loading, setLoading]       = useState(false);
    const [saving, setSaving]         = useState(false);

    // Fetch boards on mount so the selector is ready on Step 1
    useEffect(() => {
        recitalService.getBoards().then(res => {
            const b = res.data.boards || [];
            setBoards(b);
            if (b.length === 1) setBoardId(b[0].id);
        }).catch(e => { console.error(e); toast.error('Could not load your schedules. Please close and reopen this window.'); });
    }, []);

    // Fetch classes when entering Step 2
    useEffect(() => {
        if (step === 2) {
            setLoading(true);
            recitalService.getClasses(boardId || null).then(res => {
                const cls = res.data.classes || [];
                setClasses(cls);
                if (importOpt === 'all') setSelected(new Set(cls.map(c => c.id)));
            }).catch(e => { console.error(e); toast.error('Could not load your classes. Please try again.'); }).finally(() => setLoading(false));
        }
    }, [step]);

    const filtered = classes.filter(c =>
        c.className.toLowerCase().includes(search.toLowerCase())
    );

    const toggle = (id) => setSelected(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    const handleNext = () => {
        if (!name.trim()) return;
        if (importOpt === 'empty') return handleCreate([]);
        setStep(2);
    };

    const handleCreate = async (overrideSelected) => {
        setSaving(true);
        try {
            // The server snapshots the chosen classes (details AND rosters)
            // into the recital as independent copies, in one transaction.
            const ids = overrideSelected ?? [...selected];
            const body = { name: name.trim() };
            if (importOpt === 'all') {
                body.importAll = true;
                if (boardId) body.boardId = boardId;
            } else if (importOpt === 'choose' && ids.length > 0) {
                body.stickerIds = ids;
            }
            const res = await recitalService.createRecital(body);
            onCreated(res.data.recital);
        } catch (e) {
            console.error('Failed to create recital', e);
            toast.error('Could not create the recital. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
            <div style={S.modal}>
                <div style={S.header}>
                    <h2 style={S.title}>{step === 1 ? 'New Recital' : 'Select Classes'}</h2>
                    <button style={S.closeBtn} onClick={onClose}><FiX size={20}/></button>
                </div>

                {step === 1 && (
                    <>
                        <div style={S.body}>
                            <label style={S.label}>Recital name</label>
                            <input
                                style={S.input}
                                placeholder="e.g. Spring Recital 2026"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleNext()}
                                autoFocus
                            />

                            {importOpt !== 'empty' && (
                                <div style={{ marginTop: 20 }}>
                                    <label style={S.label}>Which schedule?</label>
                                    <select
                                        style={S.select}
                                        value={boardId}
                                        onChange={e => setBoardId(e.target.value)}
                                    >
                                        <option value="">All schedules</option>
                                        {boards.map(b => (
                                            <option key={b.id} value={b.id}>{b.boardName}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div style={{ marginTop: 20 }}>
                                <label style={S.label}>Classes</label>
                                <div style={S.optWrap}>
                                    {IMPORT_OPTS.map(o => (
                                        <div key={o.value} style={S.opt(importOpt === o.value)} onClick={() => setImportOpt(o.value)}>
                                            <div style={S.optTitle}>{o.title}</div>
                                            <div style={S.optSub}>{o.sub}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div style={S.footer}>
                            <button style={S.btnSecondary} onClick={onClose}>Cancel</button>
                            <button style={S.btnPrimary} onClick={handleNext} disabled={!name.trim()}>
                                {importOpt === 'empty' ? 'Create Recital' : 'Next →'}
                            </button>
                        </div>
                    </>
                )}

                {step === 2 && (
                    <>
                        <div style={S.body}>
                            {boardId && boards.length > 0 && (
                                <div style={{ fontSize:12, color:'#6b6b8a', marginBottom:10 }}>
                                    From: <strong style={{ color:'#3f3f55' }}>{boards.find(b => String(b.id) === String(boardId))?.boardName}</strong>
                                </div>
                            )}
                            <div style={S.search}>
                                <FiSearch size={14} style={S.searchIcon}/>
                                <input style={S.searchInput} placeholder="Search classes..." value={search} onChange={e => setSearch(e.target.value)}/>
                            </div>
                            <div style={{ fontSize: 12, color: '#6b6b8a', marginBottom: 10 }}>
                                {selected.size} of {classes.length} selected
                            </div>
                            {loading ? <p style={{ color:'#6b6b8a' }}>Loading classes...</p> : filtered.map(c => (
                                <div key={c.id} style={{ ...S.classRow, background: selected.has(c.id) ? '#e6f7fd' : '#f8f8fc' }} onClick={() => toggle(c.id)}>
                                    <div style={S.swatch(c.styleColor)}/>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                            <span style={S.className}>{c.className}</span>
                                            {c.dayOfTheWeek && <span style={S.dayChip}>{DAY_ABBR[c.dayOfTheWeek] || c.dayOfTheWeek}</span>}
                                        </div>
                                        <div style={S.classMeta}>
                                            {[...(c.styles||[]), ...(c.teachers||[])].join(' · ')}
                                            {' '}
                                            {c.studentCount > 0
                                                ? <span style={{ color:'#3f3f55' }}>{c.studentCount} students</span>
                                                : <span style={{ fontStyle:'italic' }}>No students added</span>}
                                        </div>
                                    </div>
                                    <div style={{ ...S.checkbox, ...(selected.has(c.id) ? S.checked : {}) }}>
                                        {selected.has(c.id) && <FiCheck size={11} color="#fff"/>}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div style={S.footer}>
                            <button style={S.btnSecondary} onClick={() => setStep(1)}>Back</button>
                            <button style={S.btnPrimary} onClick={() => handleCreate()} disabled={saving}>
                                {saving ? 'Creating...' : 'Create Recital'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
